/**
 * VoiceRecorder Component
 *
 * Records audio from microphone for voice cloning.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  VOICE_MIN_DURATION_SECONDS,
  VOICE_MAX_DURATION_SECONDS,
  VOICE_OPTIMAL_MIN_DURATION,
  VOICE_OPTIMAL_MAX_DURATION,
} from '../../services/voiceApi';

interface VoiceRecorderProps {
  /** Called with the recorded blob once a recording finishes. */
  onRecordingComplete: (audioBlob: Blob) => void;
  /**
   * Called when the recording is thrown away ("Record Again"). The parent owns
   * the selected audio, so it MUST be told to drop the discarded blob -
   * otherwise "Record Again" followed by "Create Voice" uploads the old take.
   */
  onRecordingDiscarded: () => void;
  /** Called with a translated message whenever recording fails. */
  onError: (error: string) => void;
  disabled?: boolean;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onRecordingDiscarded,
  onError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isRecordingRef = useRef<boolean>(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  /**
   * Stop recording.
   * Uses refs to avoid stale closures in setInterval callbacks.
   */
  const stopRecording = useCallback(() => {
    // Use ref to check recording state to avoid stale closure issues
    if (mediaRecorderRef.current && isRecordingRef.current) {
      // Clear timer FIRST to prevent any race conditions
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
      setIsRecording(false);
    }
  }, []);

  /**
   * Start recording.
   */
  const startRecording = useCallback(async () => {
    // Prevent starting if already recording
    if (isRecordingRef.current) {
      return;
    }

    // Clear any existing timer to prevent duplicates
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      streamRef.current = stream;
      setPermissionDenied(false);

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });

        // Create preview URL
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Notify parent
        onRecordingComplete(blob);

        // Cleanup stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      isRecordingRef.current = true;
      setIsRecording(true);
      setDuration(0);

      // Start timer - use stopRecording reference from closure
      // Since stopRecording now has no dependencies, this is stable
      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          const newDuration = prev + 1;
          // Auto-stop at max duration using ref to call stopRecording
          if (newDuration >= VOICE_MAX_DURATION_SECONDS) {
            // Clear timer and stop recording
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            if (mediaRecorderRef.current && isRecordingRef.current) {
              mediaRecorderRef.current.stop();
              isRecordingRef.current = false;
              setIsRecording(false);
            }
          }
          return newDuration;
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);

      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setPermissionDenied(true);
        onError(t('voiceClone.permissionDenied'));
      } else {
        onError(t('voiceClone.recordingError'));
      }
    }
  }, [onRecordingComplete, onError, t]);

  /**
   * Discard the recording and reset local state.
   * The parent holds the blob we handed it in `onRecordingComplete`, so it has
   * to be told to drop it too.
   */
  const resetRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setDuration(0);
    onRecordingDiscarded();
  }, [audioUrl, onRecordingDiscarded]);

  /**
   * Format duration as MM:SS.
   */
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Get duration color based on value.
   */
  const getDurationColor = (): string => {
    if (duration < VOICE_MIN_DURATION_SECONDS) return 'text-red-500';
    if (duration < VOICE_OPTIMAL_MIN_DURATION) return 'text-yellow-500';
    if (duration <= VOICE_OPTIMAL_MAX_DURATION) return 'text-green-500';
    if (duration <= VOICE_MAX_DURATION_SECONDS) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="voice-recorder p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
      {/* Instructions */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
        <p className="font-medium mb-2">{t('voiceClone.recordingInstructions')}</p>
        <ul className="list-disc list-inside space-y-1">
          <li>{t('voiceClone.instruction1')}</li>
          <li>{t('voiceClone.instruction2')}</li>
          <li>{t('voiceClone.instruction3')}</li>
        </ul>
      </div>

      {/* Duration Display */}
      <div className="text-center mb-4">
        <span className={`text-4xl font-mono ${getDurationColor()}`}>
          {formatDuration(duration)}
        </span>
        <p className="text-xs text-gray-500 mt-1">
          {t('voiceClone.optimalDuration', {
            min: VOICE_OPTIMAL_MIN_DURATION,
            max: VOICE_OPTIMAL_MAX_DURATION,
          })}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
        <div
          className={`h-2 rounded-full transition-all ${
            duration < VOICE_OPTIMAL_MIN_DURATION
              ? 'bg-yellow-500'
              : duration <= VOICE_OPTIMAL_MAX_DURATION
                ? 'bg-green-500'
                : 'bg-yellow-500'
          }`}
          style={{ width: `${Math.min((duration / VOICE_MAX_DURATION_SECONDS) * 100, 100)}%` }}
        />
      </div>

      {/* Permission Denied Warning */}
      {permissionDenied && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded">
          {t('voiceClone.permissionDeniedHelp')}
        </div>
      )}

      {/* Controls */}
      <div className="flex justify-center gap-4">
        {!isRecording && !audioUrl && (
          <button
            onClick={startRecording}
            disabled={disabled}
            className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center gap-2 disabled:opacity-50"
          >
            <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
            {t('voiceClone.startRecording')}
          </button>
        )}

        {isRecording && (
          <button
            onClick={stopRecording}
            disabled={duration < VOICE_MIN_DURATION_SECONDS}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-full flex items-center gap-2 disabled:opacity-50"
          >
            <span className="w-3 h-3 bg-white" />
            {t('voiceClone.stopRecording')}
          </button>
        )}

        {audioUrl && (
          <>
            <button
              onClick={resetRecording}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
            >
              {t('voiceClone.recordAgain')}
            </button>
          </>
        )}
      </div>

      {/* Audio Preview */}
      {audioUrl && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{t('voiceClone.preview')}</p>
          <audio src={audioUrl} controls className="w-full" />
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
